import { useCallback, useEffect, useState } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Building2, Plus, Pencil, Trash2, Tv } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Accounts (Cuentas / Clientes) page.
 *
 * One row per business. From here the admin can create, edit, and delete the
 * accounts that own TVs / playlists / media. Deleting an account cascades to
 * its TVs, playlists, media, etc. (enforced by FK ON DELETE CASCADE in DB).
 */
export default function AccountsPage() {
  const { businesses, refreshBusinesses } = useBusiness();
  const [counts, setCounts] = useState({});      // {businessId: {tvs, playlists, media}}
  const [editing, setEditing] = useState(null);  // business row or {} for new
  const [confirmDel, setConfirmDel] = useState(null);

  const loadCounts = useCallback(async () => {
    if (!businesses?.length) return;
    const results = await Promise.all(
      businesses.map(async (b) => {
        const [tv, pl, m] = await Promise.all([
          apiAdmin.get(`/tvs?business_id=${b.id}`).then((r) => r.data?.length || 0).catch(() => 0),
          apiAdmin.get(`/playlists?business_id=${b.id}`).then((r) => r.data?.length || 0).catch(() => 0),
          apiAdmin.get(`/media?business_id=${b.id}`).then((r) => r.data?.length || 0).catch(() => 0),
        ]);
        return [b.id, { tvs: tv, playlists: pl, media: m }];
      })
    );
    setCounts(Object.fromEntries(results));
  }, [businesses]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const onDelete = async () => {
    if (!confirmDel) return;
    try {
      await apiAdmin.delete(`/businesses/${confirmDel.id}`);
      toast.success(`Account "${confirmDel.name}" deleted`);
      setConfirmDel(null);
      await refreshBusinesses();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Cuentas</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Accounts
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            One account per venue / client. Devices, playlists, and media are scoped to each account.
          </p>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => setEditing({})}
          data-testid="account-create-button"
        >
          <Plus className="h-4 w-4 mr-2" /> New account
        </Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="accounts-list">
        {(businesses || []).length === 0 && (
          <div className="px-6 py-12 text-center text-sm text-slate-500">
            No accounts yet. Click <span className="font-semibold">New account</span> to add your first one.
          </div>
        )}
        {(businesses || []).map((b) => {
          const c = counts[b.id] || {};
          return (
            <div
              key={b.id}
              className="px-4 py-3 flex items-center gap-3"
              data-testid={`account-row-${b.id}`}
            >
              <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-700 grid place-items-center shrink-0">
                <Building2 className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate" data-testid={`account-name-${b.id}`}>
                  {b.name}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {b.address || b.description || "No address"}
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-4 text-xs text-slate-600 mr-3">
                <span className="inline-flex items-center gap-1.5">
                  <Tv className="h-3.5 w-3.5 text-slate-400" /> {c.tvs ?? "–"} devices
                </span>
                <span className="tabular-nums">{c.playlists ?? "–"} playlists</span>
                <span className="tabular-nums">{c.media ?? "–"} media</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(b)}
                data-testid={`account-edit-${b.id}`}
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-rose-200 text-rose-600 hover:bg-rose-50"
                onClick={() => setConfirmDel(b)}
                data-testid={`account-delete-${b.id}`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
              </Button>
            </div>
          );
        })}
      </div>

      <AccountEditor
        business={editing}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await refreshBusinesses();
          await loadCounts();
        }}
      />

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="max-w-md" data-testid="account-delete-confirm">
          <DialogHeader>
            <DialogTitle>Delete account?</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Permanently delete <span className="font-semibold">{confirmDel?.name}</span> and ALL its devices,
              playlists, schedules, and media. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={onDelete}
              data-testid="account-delete-confirm-button"
            >
              Yes, delete forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AccountEditor({ business, onClose, onSaved }) {
  const isNew = business && !business.id;
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (business) {
      setName(business.name || "");
      setAddress(business.address || "");
      setDescription(business.description || "");
    }
  }, [business]);

  if (!business) return null;

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        address: address.trim(),
        description: description.trim(),
      };
      if (isNew) {
        await apiAdmin.post("/businesses", body);
      } else {
        await apiAdmin.patch(`/businesses/${business.id}`, body);
      }
      toast.success(isNew ? "Account created" : "Account updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!business} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="account-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New account" : "Edit account"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Each account groups its own devices, playlists, and media.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Roy Restaurant"
              autoFocus
              data-testid="account-name-input"
            />
          </div>
          <div>
            <Label className="text-xs">Address</Label>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g. 2412 Tremont Ave, NY"
              data-testid="account-address-input"
            />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Internal notes (optional)"
              data-testid="account-description-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="account-editor-submit"
          >
            {saving ? "Saving…" : isNew ? "Create account" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
